'use client';

import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { IconGallery } from 'src/general/Icons/IconGallery';
import { IconImperfections } from 'src/general/Icons/IconImperfections';
import { IconInterior } from 'src/general/Icons/IconInterior';
import { IconThumbsUp } from 'src/general/Icons/IconThumbsUp';
import { Image } from 'src/general/components/Image/Image';
import { formatCarGallery } from 'src/pdp/sections/Pdp/pdpHelpers';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { DataLayerPdpEvent, pushToDataLayerPDP } from 'src/utils/pushToDataLayerPDP';
import styles from './CarGallery.module.scss';

interface GalleryItem {
  imageSrc: string;
}

interface Props {
  productDetails: ProductDetailsPayload;
  microcopy: {
    title: ReactNode;
    noImperfections: ReactNode;
    noImperfectionsMobile: ReactNode;
  };
}

export const CarGallery: FC<Props> = ({ productDetails, microcopy }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const { gallery, imperfection, interior } = formatCarGallery(productDetails);

  return (
    <section className={styles.root}>
      <h3 className={styles.header}>{microcopy.title}</h3>
      <div className={clsx(styles.content, { [styles.noImperfections]: !imperfection })}>
        <GalleryItemTrigger
          {...gallery}
          testId="PDP_GALLERY_BUTTON_GALLERY"
          label="Gallery"
          icon={<IconGallery />}
          className={styles.triggerGallery}
          priority
          onClick={() => {
            setCurrentModal({
              name: 'gallery',
              initialState: 'gallery',
            });
            pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
              sku: productDetails.sku,
              option: 'GALLERY',
            });
          }}
        />
        {!imperfection ? (
          <div className={styles.noImperfection}>
            <IconThumbsUp />
            <span className={clsx(styles.mdUp, 'c-fs-caption')}>{microcopy.noImperfections}</span>
            <span className={clsx(styles.mdDown, 'c-fs-caption')}>{microcopy.noImperfectionsMobile}</span>
          </div>
        ) : (
          <GalleryItemTrigger
            {...imperfection}
            testId="PDP_GALLERY_BUTTON_IMPERFECTION"
            label="Imperfections"
            icon={<IconImperfections />}
            className={styles.triggerImperfection}
            priority
            onClick={() => {
              setCurrentModal({
                name: 'gallery',
                initialState: 'imperfections',
              });
              pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
                sku: productDetails.sku,
                option: 'IMPERFECTIONS',
              });
            }}
          />
        )}
        <GalleryItemTrigger
          {...interior}
          testId="PDP_GALLERY_BUTTON_INTERIOR"
          label="Interior"
          icon={<IconInterior />}
          className={styles.triggerInterior}
          onClick={() => {
            setCurrentModal({
              name: 'gallery',
              initialState: 'interior',
            });
            pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
              sku: productDetails.sku,
              option: 'INTERIOR',
            });
          }}
        />
      </div>
    </section>
  );
};

const GalleryItemTrigger: FC<
  GalleryItem & {
    testId: string;
    label: string;
    icon: ReactNode;
    onClick: () => void;
    className?: string;
    priority?: boolean;
  }
> = ({ imageSrc, onClick, testId, label, icon, className, priority }) => {
  let imageTestId: string | undefined;
  if (testId === 'PDP_GALLERY_BUTTON_GALLERY') {
    imageTestId = 'gallery-button-img';
  } else if (testId === 'PDP_GALLERY_BUTTON_IMPERFECTION') {
    imageTestId = 'imperfection-button-img';
  } else {
    imageTestId = undefined;
  }

  return (
    <button
      type="button"
      aria-label={`View ${label}`}
      className={clsx(styles.item, className)}
      onClick={onClick}
      data-testid={testId}
    >
      <Image
        src={imageSrc}
        className={styles.itemImage}
        alt=""
        width={660}
        height={330}
        priority={priority}
        data-testid={imageTestId}
      />
      <span className={styles.itemDescription}>
        <span className={styles.itemIcon} aria-hidden="true">
          {icon}
        </span>
        {label}
      </span>
    </button>
  );
};
